/**
 * Provider factory — the single place the app resolves which intelligence
 * engine to use. Swap or extend providers here (e.g. Claude, backend relay)
 * without touching any UI code.
 */

import type { AiProvider } from '../types';
import { LocalProvider } from './localProvider';
import { GeminiProvider } from './geminiProvider';

/** Gemini API keys are either the classic "AIza…" Google API key format or
 * the newer "AQ.…" format Google began issuing in 2025. Anything else (e.g.
 * a browser-autofilled password) must never silently activate the cloud path. */
function looksLikeGeminiKey(key: string | undefined): key is string {
  if (typeof key !== 'string') return false;
  const k = key.trim();
  return /^AIza[\w-]{10,}$/.test(k) || /^AQ\.[\w-]{20,}$/.test(k);
}

/**
 * `hasConsent` is REQUIRED (17 Oct 2026): the store passes its AI-consent
 * check and every cloud provider refuses to send without it. The on-device
 * LocalProvider needs no consent — nothing leaves the device.
 */
export function resolveProvider(runtimeApiKey: string | undefined, hasConsent: () => boolean): AiProvider {
  // Production path: backend relay (Vertex AI, credentials server-side,
  // no-training terms). Takes precedence over any client-side key.
  const relay = (import.meta.env.VITE_AI_RELAY_URL as string | undefined)?.trim();
  if (relay && /^https:\/\//.test(relay)) {
    return new GeminiProvider({
      apiKey: '',
      endpoint: relay,
      getAuthToken: () => import('../sync/firebaseSync').then((m) => m.getAiAuthToken()),
      hasConsent,
    });
  }
  // Dev fallback: direct Gemini API with a local key.
  const runtime = runtimeApiKey?.trim();
  const env = (import.meta.env.VITE_GEMINI_API_KEY as string | undefined)?.trim();
  const key = looksLikeGeminiKey(runtime) ? runtime : looksLikeGeminiKey(env) ? env : undefined;
  if (key) return new GeminiProvider({ apiKey: key, hasConsent });
  return new LocalProvider();
}

export { LocalProvider } from './localProvider';
export { GeminiProvider } from './geminiProvider';
